/** @module csi/view/_Splitter */
define([
	"dojo/_base/declare",
	"dojo/dom",
	"dojo/dom-attr",
	"dojo/query",
	"csi",
	"csi/_base/underscore",
	"csi/html",
	"csi/printUtil"
], function(declare, dom, domAttr, query, csi, _, html, printUtil) {

	var CONTAINER_ID = "__splitterContainer";

	return declare(null, {
		// String: DOM query to use to get container node of view when not on first page; once DOM gets copied from
		// one page to the next, can no longer rely on attach points of view
		//containerQuery: "",

		/**
		 * @property {Number} nRows - number of rows in view
		 */
		nRows: 0,

		/**
		 * @property {DOMNode[]} rowContainers - map from row index to new container
		 */
		rowContainers: null,

		/**
		 * @property {Object} rowHeightMap - map from row index to height
		 */
		rowHeightMap: null,

		/**
		 * @property {DOMNode[]} containers - holds all containers that the main container was split into.
		 */
		containers: null,

		/**
		 * @property {Object} rowMap - map from row index to element index
		 */
		rowMap: null,

		constructor: function() {
			this.containers = [];
			this.rowContainers = [];
			this.rowHeightMap = {};
		},

		/**
		 * Remove child DOMNodes outside the given range [beginning,end], but preserve those that are frozen if specified
		 * @param {DOMNode} parent
		 * @param {number} beginning
		 * @param {number} end
		 * @param {number} [nFrozen]
		 */
		_removeAllChildrenExceptGivenRange: function(parent, beginning, end, nFrozen) {
			var children = parent.children;
			var length = children.length;
			var i;

			nFrozen = nFrozen || 0;

			// we start removing from the last element so indices of children do not change as we go
			for (i = length - 1; i > end; i--) {
				parent.removeChild(children[i]);
			}

			for (i = beginning - 1; i >= nFrozen; i--) {
				parent.removeChild(children[i]);
			}
		},

		/**
		 * Split composite view content so it fits nicely on pages with given width and height
		 * @param {DOMNode[]} aPages
		 * @param {DOMNode} viewRoot
		 * @param {number} nPageHeight
		 * @param {number} nPageWidth
		 * @param {Function} [copyCallback] - function called when child view is copied from one page to another
		 */
		split: function(aPages, viewRoot, nPageHeight, nPageWidth, copyCallback) {
			var eContainer = this._getContainerNode(viewRoot);
			var eView = aPages.last();
			var refNodes = this._getRefNodes(eContainer);
			var contentWidth = printUtil.getContentWidth();

			// remove refNodes from current view temporarily so we don't spend time cloning them
			html.removeRange(refNodes[0]);

			var newPage = this._prepareFirstPage(aPages, eView, eContainer, nPageWidth, contentWidth, nPageHeight, refNodes);
			if (newPage) {
				eView = newPage.view;
				eContainer = newPage.container;
			}

			// first page overhead is equal to remaining height of view
			var firstPageHeightOverhead = eView.offsetHeight;
			var otherPageHeightOverhead = this._getOtherPageHeightOverhead(eView, copyCallback);

			// add content back to page now that overhead is calculated
			this._addRefNodesToView(refNodes, eContainer);

			// view-specific preprocessing
			this._preSplit(eContainer, refNodes, nPageHeight - otherPageHeightOverhead);

			// if view fits on page, then nothing more to do
			if (this._splittingNotRequired(eView, contentWidth, nPageHeight, nPageWidth)) {
				this._prepareDataForSinglePagePrint(eView, eContainer, nPageWidth);
				return;
			}

			// <td> can affect minimum width of the headings, so we have to build the
			// columnWidthMap before the rows (table body) are removed from the container.
			this._buildColumnWidthMap(nPageWidth, eView);

			// record item row information
			this._buildRowMaps(refNodes, nPageHeight, otherPageHeightOverhead, firstPageHeightOverhead, nPageWidth);

			// remove refNodes from current view so we don't incur cost of copying and removing them
			html.removeRange(refNodes[0]);

			// split the headings horizontally between number of containers needed
			// by copying the containers and assigning them to the right container (Only applies to Table at the moment)
			// The split of headings should happen AFTER refNodes are removed from the <body>. Otherwise we'll be appending to existing rows
			this._splitHeadings(aPages, eView, eContainer, copyCallback, nPageWidth);

			// iterate over rows until all rows allocated to a page
			this._applyRowsToContainers(aPages, eView, eContainer, copyCallback, nPageHeight, otherPageHeightOverhead, firstPageHeightOverhead, viewRoot, refNodes);
		},

		/**
		 * This method adds refNodes back to the view.
		 * @param {DOMNode[]} refNodes - consists all rows in the table
		 * @param {DOMNode} eContainer - the current view
		 */
		_addRefNodesToView: function(refNodes, eContainer) {
			_.each(refNodes, function(refNode) {
				eContainer.appendChild(refNode);
			});
		},

		/**
		 * Return DOMNodes of container representing view results
		 * @param {DOMNode} eContainer
		 * @return {NodeList}
		 */
		_getRefNodes: function(eContainer) {
			console.assert(false);
		},

		/**
		 * This method decides where the view should start from. Whether we need a new page to start from or
		 * we can just use the current page.
		 * @param {DOMNode} aPages - contains all pages
		 * @param {DOMNode} eView - this is the last page
		 * @param {Number} nPageWidth - page width
		 * @param {Number} contentWidth - content width
		 * @param {Number} nPageHeight - page height
		 * @param {DOMNode} refNodes - consists all rows in the table
		 */
		_prepareFirstPage: function(aPages, eView, eContainer, nPageWidth, contentWidth, nPageHeight, refNodes) {
		},

		_prepareDataForSinglePagePrint: function(eView, eContainer, nPageWidth) {
			// summary: For the case where table fits on the paper, then we need to prepare some data for footer.
		},

		_buildColumnWidthMap: function(nPageWidth, eView) {
			// summary: prepares an array that consists of the index of columns and the minimum width for that column.
		},

		_splittingNotRequired: function(eView, contentWidth, nPageHeight, nPageWidth) {
			return eView.offsetHeight <= nPageHeight;
		},

		_splitHeadings: function(aPages, eView, eContainer, copyCallback, nPageWidth) {
			// summary: this method splits table headings into one set of containers.
		},

		_applyRowsToContainers: function(aPages, eView, eContainer, copyCallback, nPageHeight, otherPageHeightOverhead, firstPageHeightOverhead, viewRoot, refNodes) {
			var availableHeight = nPageHeight - firstPageHeightOverhead;
			var bCanRemoveAllOnFirstPage = (otherPageHeightOverhead < firstPageHeightOverhead);
			var bFirstCopy = true;
			var i;
			var n;

			for (i = 0; i < this.nRows; ++i) {
				// advance to next page if height exceeds available space on page and either this is not the first page or
				// the next page won't have any more space either
				if ((this.rowHeightMap[i] > availableHeight) && (i || bCanRemoveAllOnFirstPage)) {
					availableHeight = nPageHeight - otherPageHeightOverhead;

					// copy page
					var copyResult = this._copyPageWithContainer(aPages, eView, eContainer, false, copyCallback);
					eView = copyResult.viewNode;
					eContainer = copyResult.containerNode;

					if (bFirstCopy) {
						// if nothing on first page, then remove it as it will appear on subsequent pages
						if (copyCallback && !i) {
							// remove view title if there is one
							var previous = viewRoot.previousSibling;
							if ($(previous).hasClass(html.CSS_VIEW_TITLE)) {
								html.orphan(previous);
							}

							// remove view
							html.orphan(viewRoot);
						}

						bFirstCopy = false;
					}
				}

				availableHeight -= this.rowHeightMap[i];
				this.rowContainers[i] = eContainer;
			}

			// add rows to appropriate page
			for (i = 0; i < this.nRows; ++i) {
				eContainer = this.rowContainers[i];
				n = this.rowMap[i + 1] || refNodes.length;

				for (var j = this.rowMap[i]; j < n; ++j) {
					eContainer.appendChild(refNodes[j]);
				}
			}
		},

		/**
		 * Extension point to populate nRows, rowMap and rowHeightMap
		 * @param {DOMNode[]} refNodes - all rows
		 * @param {Number} nPageHeight - height of print page
		 * @param {Number} otherPageHeightOverhead - reserved height on all pages but first
		 * @param {Number} firstPageHeightOverhead - reserved height on first page
		 * @param {Number} nPageWidth - width of the print page
		 */
		_buildRowMaps(refNodes, nPageHeight, otherPageHeightOverhead, firstPageHeightOverhead, nPageWidth) { },

		_copyPageWithContainer: function(/*DOMNode[]*/aPages, /*DOMNode*/viewNode, /*DOMNode*/containerNode, /*Boolean*/ append, /*Function?*/copyCallback) {
			// summary: copy view to create another page, returning object containing new view and container within that view
			var containerNodeCopy;
			var viewNodeCopy;

			domAttr.set(containerNode, "id", CONTAINER_ID);
			viewNodeCopy = html.cloneElement(viewNode);
			domAttr.set(containerNode, "id", "");
			aPages.push(viewNodeCopy);

			if (copyCallback) {
				copyCallback(viewNode, viewNodeCopy);
			}

			if (append) {
				viewNode.parentNode.appendChild(viewNodeCopy);
			} else {
				viewNode.parentNode.insertBefore(viewNodeCopy, viewNode.nextSibling);
			}

			containerNodeCopy = dom.byId(CONTAINER_ID) || viewNodeCopy;
			domAttr.set(containerNodeCopy, "id", "");

			return {viewNode: viewNodeCopy, containerNode: containerNodeCopy};
		},

		_getContainerNode: function(/*DOMNode*/viewRoot) {
			// summary: get domNode containing list of nodes to split
			return query(this.containerQuery, viewRoot)[0] || viewRoot;
		},

		_getOtherPageHeightOverhead: function(/*DOMNode*/eView, /*Function?*/copyCallback) {
			// summary: given page that may contain parts of other views already split, create copy
			// and remove those extra parts, giving page with only current view to be split; from that
			// the overhead height for subsequent pages can be determined. If not in composite view (no
			// copy callback), then no other views will be on page.
			var h;

			if (copyCallback) {
				// clone view to determine overhead on subsequent pages
				var eTemp = html.cloneElement(eView);
				copyCallback(null, eTemp);
				eView.parentNode.appendChild(eTemp);
				h = eTemp.offsetHeight;
				eView.parentNode.removeChild(eTemp);
			} else {
				h = eView.offsetHeight;
			}

			return h;
		},

		_preSplit: function(/*DOMNode*/eContainer, /*DOMNode[]*/refNodes, /*Number*/nMaxPageHeight) {
			// summary: extension point to process DOM before splitting
		}
	});
});